import React, { memo, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { View } from 'react-native';
import Image from 'utils/packages/rnFastImage';
import Text from 'design-system/Text';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import { IconLock } from 'design-system/icons';
import { CardView } from 'design-system/CardView';
import IconTicketSolid from 'design-system/icons/IconTicketSolid';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
const DayTradeCard = ({ cardItem }) => {
    const styles = useStyles(styleSet);
    if ('type' in cardItem) {
        if (cardItem.type === 'EMPTY_SLOT') {
            return (<View style={styles.eachCardContainer} testID="emptySlotCard">
          <CardView noMargin style={styles.eachCardView}>
            <View style={styles.emptyImgContainer}>
              <IconTicketSolid colorKey="secondary" height={13} width={13}/>
            </View>
            <Spacer h={8}/>
            <View style={styles.symbolShimmer}/>
            <View style={styles.dateShimmer}/>
          </CardView>
        </View>);
        }
        if (cardItem.type === 'LOCKED_SLOT') {
            return (<View style={styles.eachCardContainer} testID="lockedSlotCard">
          <CardView noMargin style={styles.eachCardView}>
            <View style={styles.lockedImgContainer}>
              <IconLock height={13} width={13}/>
            </View>
            <Spacer h={8}/>
            <View style={styles.symbolShimmer}/>
            <View style={styles.dateShimmer}/>
          </CardView>
        </View>);
        }
    }
    return (<View style={styles.eachCardContainer} testID="dayTradeCard">
      <CardView noMargin style={styles.eachCardView}>
        <View style={styles.imgContainer}>
          <Image resizeMode="contain" source={{ uri: cardItem.iconUrl }} style={styles.stockIconImg}/>
        </View>
        <Spacer h={8}/>
        <Text Text-14>{cardItem.symbol}</Text>
        <Text TextThin-14>{format(parseISO(cardItem.date), 'MMM d')}</Text>
      </CardView>
    </View>);
};
const DayTradeCards = () => {
    const styles = useStyles(styleSet);
    const stockCache = useAppSelector((state) => state.invest.caches);
    const filledDayTrades = useAppSelector((state) => state.unpersistedInvestReducer.filledDayTrades);
    const pendingDayTrades = useAppSelector((state) => state.unpersistedInvestReducer.pendingDayTrades);
    const dayTradeCards = useMemo(() => {
        if (filledDayTrades && pendingDayTrades) {
            const mergedArray = [...filledDayTrades, ...pendingDayTrades].map((dayTrade) => ({
                ...dayTrade,
                iconUrl: stockCache[dayTrade.assetId]?.iconUrl,
            }));
            while (mergedArray.length < 3) {
                mergedArray.push({ type: 'EMPTY_SLOT' });
            }
            mergedArray.push({ type: 'LOCKED_SLOT' });
            return mergedArray;
        }
        return undefined;
    }, [filledDayTrades, pendingDayTrades, stockCache]);
    return (<View style={styles.container}>
      {dayTradeCards?.map((dayTradeCard, index) => (<DayTradeCard cardItem={dayTradeCard} key={String(index)}/>))}
    </View>);
};
export default memo(DayTradeCards);
const styleSet = createStyleSheets((colors) => ({
    container: {
        flexDirection: 'row',
        paddingHorizontal: rem(12),
    },
    eachCardContainer: {
        flex: 1,
        paddingHorizontal: rem(4),
    },
    eachCardView: {
        flex: 1,
        alignItems: 'center',
        paddingVertical: rem(12),
        backgroundColor: colors.background.dark,
    },
    lockedImgContainer: {
        width: rem(24),
        height: rem(24),
        alignItems: 'center',
        borderRadius: rem(24),
        justifyContent: 'center',
        backgroundColor: colors.gradients.apricot.colors[0],
    },
    imgContainer: {
        width: rem(24),
        height: rem(24),
        borderRadius: rem(24),
        backgroundColor: colors.borders.divider,
    },
    emptyImgContainer: {
        width: rem(24),
        height: rem(24),
        alignItems: 'center',
        borderRadius: rem(24),
        justifyContent: 'center',
        backgroundColor: colors.borders.divider,
    },
    stockIconImg: {
        width: rem(24),
        height: rem(24),
        borderRadius: rem(24),
        backgroundColor: colors.elements.white,
    },
    symbolShimmer: {
        width: rem(40),
        height: rem(12),
        marginTop: rem(6),
        borderRadius: rem(4),
        backgroundColor: colors.borders.divider,
    },
    dateShimmer: {
        width: rem(35),
        height: rem(9),
        marginTop: rem(5),
        borderRadius: rem(4),
        backgroundColor: colors.borders.divider,
    },
}));
